import * as React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import LoadingData from './LoadingData';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { processPercentileData, addTotalsRow, PercentileData } from './utils/percentileDataProcessor';

interface HistogramSummaryGridProps {
  percentileData: PercentileData | null;
  loading: boolean;
  consumptionType?: number; // 1 for electricity, 2 for gas
  displayMode?: 'consumption' | 'cost';
}

const getRowId = (row: any) => row.percentile;

const HistogramSummaryGrid: React.FC<HistogramSummaryGridProps> = ({ 
  percentileData, 
  loading,
  consumptionType = 1,
  displayMode = 'consumption'
}) => {  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dataGridRef = React.useRef<HTMLDivElement>(null);   
  
  // Create theme-aware column definitions
  const getThemeAwareColumns = (): GridColDef[] => {
    const columns: GridColDef[] = [
        { 
          field: 'percentile', 
          headerName: 'Category',
          flex: 1.5, 
          minWidth: 80,
          renderCell: (params: any) => {
            const color = params.row.color;
            return <div style={{ textAlign: 'left', width: '100%', color: color, fontWeight: 'bold' }}>{params.value}</div>;
          }
        },
        {
          field: 'duration',
          headerName: 'Duration',
          headerAlign: 'center',
          flex: 1,
          minWidth: 100,
          renderCell: (params: any) => {
            if (params.value === null || params.value === undefined) {
              return <div style={{ textAlign: 'center', width: '100%' }}></div>;
            }
            return <div style={{ textAlign: 'center', width: '100%' }}>{params.value}</div>;
          }
        },
     ];

     if (displayMode === 'cost') {
       columns.push({
         field: 'total_cost',
         headerName: 'Cost (£)',
         headerAlign: 'center',
         flex: 1,
         minWidth: 90,
         renderCell: (params: any) => {
           if (params.value === null || params.value === undefined) {
             return <div style={{ textAlign: 'center', width: '100%' }}></div>;
           }
           return <div style={{ textAlign: 'center', width: '100%' }}>£{params.value.toFixed(2)}</div>;
         }
       });
     } else {
       columns.push(
         {
           field: 'total_consumption',
           headerName: 'Usage (kWh)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 90,
           renderCell: (params: any) => {
             if (params.value === null || params.value === undefined) {
               return <div style={{ textAlign: 'center', width: '100%' }}></div>;
             }
             return <div style={{ textAlign: 'center', width: '100%' }}>{params.value.toFixed(2)}</div>;
           }
         },
         {
           field: 'total_emissions',
           headerName: 'Emissions (kg)',
           headerAlign: 'center',
           flex: 1,
           minWidth: 90,
           renderCell: (params: any) => {
             if (params.value === null || params.value === undefined) {
               return <div style={{ textAlign: 'center', width: '100%' }}></div>;
             }
             const kgValue = params.value / 1000;
             return <div style={{ textAlign: 'center', width: '100%' }}>{kgValue.toFixed(2)}</div>;
           }
         }
       );
     }

     return columns;
   };

     // Process data to create summary rows
   const processSummaryData = () => {
     if (!percentileData) return [];
     
     const consumptionTypeName = consumptionType === 1 ? 'Electricity' : 'Gas';
     const processedData = processPercentileData(percentileData, consumptionTypeName, theme, isMobile, displayMode);
     return addTotalsRow(processedData);
   };

     const summaryData = processSummaryData();
   
   const columns = getThemeAwareColumns();

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LoadingData open={loading} />
      
      {percentileData && summaryData.length > 0 && (        
            <DataGrid
               ref={dataGridRef}
               sx={{
                 // Disable MUI DataGrid's mobile-specific behavior
                 "& .MuiDataGrid-root": {
                   overflowX: "hidden !important"
                 },
                 "& .MuiDataGrid-main": {
                   overflowX: "hidden !important"
                 },
                 "& .MuiDataGrid-virtualScroller": {
                   overflowX: "hidden !important"
                 },
                 "& .MuiDataGrid-columnHeaderTitle": {
                   whiteSpace: "normal",
                   lineHeight: "normal"
                 },
                 "& .MuiDataGrid-columnHeader": {
                   height: "unset !important"
                 },
                 "& .totals-row": {
                   backgroundColor: theme.palette.background.paper,
                   fontWeight: 'bold',
                 },
                 position: 'relative'
               }}
               autoHeight
               rows={summaryData}
               columns={columns}
               rowHeight={50}
               getRowId={getRowId}
               getRowClassName={(params) => {
                 if (params.row.percentile === 'Total') {
                   return 'totals-row';
                 }
                 return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
               }}
               disableColumnResize
               density="compact"
               hideFooter
               slotProps={{
                 filterPanel: {
                   filterFormProps: {
                     logicOperatorInputProps: {
                       variant: 'outlined',
                       size: 'small',
                     },
                     columnInputProps: {
                       variant: 'outlined',
                       size: 'small',
                       sx: { mt: 'auto' },
                     },
                     operatorInputProps: {
                       variant: 'outlined',
                       size: 'small',
                       sx: { mt: 'auto' },
                     },
                     valueInputProps: {
                       InputComponentProps: {
                         variant: 'outlined',
                         size: 'small',
                       },
                     },
                   },
                 },
               }}
             />
      )}
      
      {!loading && (!percentileData || Object.keys(percentileData).length === 0) && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100px',
          color: theme.palette.text.secondary 
        }}>
          <Typography variant="body1">
            No percentile data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default HistogramSummaryGrid;
